import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API = 'https://tradebot-k4nt.onrender.com/api';

/* ── mirrors handelSubmitUpdatePin in index.js exactly ──
   POST /api/payment/updatePin/web
   body: { username, oldpin, pin }
   oldpin is "" when a user never set a PIN (Google sign-up, pin field === "")
────────────────────────────────────────────────────────── */
const updatePinRequest = async (username, oldpin, pin) => {
  const res = await fetch(`${API}/payment/updatePin/web`, {
    method: 'POST',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, oldpin, pin }),
  });
  const data = await res.json();
  return { status: res.status, data };
};

export default function ChangePin() {
  const navigate = useNavigate();
  const { user: ctxUser, fetchUserPublic } = useUser();

  const [loading,     setLoading]     = useState(false);
  const [fetching,    setFetching]    = useState(true);   // loading user data on mount
  const [success,     setSuccess]     = useState(false);
  const [hasPin,      setHasPin]      = useState(false);  // true = user already has a pin set

  const [oldPin,      setOldPin]      = useState('');
  const [newPin,      setNewPin]      = useState('');
  const [confirmPin,  setConfirmPin]  = useState('');

  const [showOld,     setShowOld]     = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!ctxUser?.telegramName) { navigate('/login'); return; }

    // Fetch live user data from GET /api/users/{username}
    // This gives us the real `pin` field, regardless of how they signed up.
    // If pin === "" or is absent → Google user who hasn't set a pin yet → no old PIN needed.
    // If pin is a non-empty string → user has a pin → old PIN required first.
    const load = async () => {
      setFetching(true);
      const info = await fetchUserPublic(ctxUser.telegramName);
      if (info) {
        const pinValue = info.pin;
        setHasPin(typeof pinValue === 'string' && pinValue.trim() !== '');
      } else {
        // Fallback to whatever is in context
        const pinValue = ctxUser?.pin;
        setHasPin(typeof pinValue === 'string' && pinValue.trim() !== '');
      }
      setFetching(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxUser?.telegramName]);

  /* ── pin strength indicator ── */
  const pinStrength = (pin) => {
    if (!pin) return null;
    if (pin.length < 6) return { label: 'Too short', color: 'bg-red-400', textColor: 'text-red-600' };
    const allSame  = /^(.)\1+$/.test(pin);
    const sequence = /^(012345|123456|234567|345678|456789|987654|876543|765432|654321|543210)$/.test(pin);
    if (allSame || sequence) return { label: 'Weak — avoid patterns', color: 'bg-amber-400', textColor: 'text-amber-600' };
    return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600' };
  };

  const strength = pinStrength(newPin);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // mirrors: if (getNewPin === getConPin)
    if (newPin !== confirmPin) {
      toast.error('UNRECOGNISE PIN — new PIN and confirm PIN do not match');
      return;
    }
    if (newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }
    if (hasPin && (oldPin.length !== 6 || !/^\d{6}$/.test(oldPin))) {
      toast.error('Old PIN must be exactly 6 digits');
      return;
    }

    setLoading(true);

    try {
      // If user has no pin (Google + never set one), pass "" as oldpin
      // mirrors index.js: the old password is an empty string for Google sign-ups
      const oldPinValue = hasPin ? oldPin : '';

      const { status, data } = await updatePinRequest(
        ctxUser.telegramName,
        oldPinValue,
        newPin
      );

      if (status === 200) {
        setSuccess(true);
        toast.success('PIN updated successfully!');
        // Refresh context so stored pin is current
        await fetchUserPublic(ctxUser.telegramName);
      } else {
        // mirrors: handelErrorView(data.error)
        toast.error(data?.error || 'Failed to update PIN');
      }
    } catch {
      toast.error('NETWORK ERROR — please try again');
    } finally {
      setLoading(false);
    }
  };

  const PinInput = ({ id, label, value, onChange, show, onToggle, placeholder = '6-digit PIN' }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          id={id}
          type={show ? 'text' : 'password'}
          inputMode="numeric"
          pattern="\d*"
          value={value}
          onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={placeholder}
          required
          className="w-full pl-11 pr-12 py-3.5 border-2 border-slate-200 rounded-xl focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 text-center text-xl tracking-[0.4em] font-bold transition-all"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  /* ── success screen ── */
  if (success) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">PIN Updated!</h2>
          <p className="text-slate-500 mb-8">Your security PIN has been changed successfully.</p>
          <button
            onClick={() => navigate('/settings')}
            className="w-full py-3.5 bg-slate-900 text-white font-semibold rounded-xl hover:bg-slate-800 transition-all shadow-sm"
          >
            Back to Settings
          </button>
        </div>
      </DashboardLayout>
    );
  }

  /* ── loading skeleton ── */
  if (fetching) {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto px-4 py-16 flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading account info…</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-md mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-600"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Update PIN</h1>
            <p className="text-sm text-slate-500">@{ctxUser?.telegramName}</p>
          </div>
        </div>

        {/* Google / no-pin notice */}
        {!hasPin && (
          <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
              <Info size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-900 text-sm">No PIN Set Yet</p>
              <p className="text-sm text-blue-700 mt-1">
                You signed up with Google so you don't have an existing PIN.
                Just set your new PIN below — no old PIN is required.
              </p>
            </div>
          </div>
        )}

        {/* PIN tip */}
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start gap-2.5">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            Use a 6-digit PIN you'll remember. Avoid sequences like <strong>123456</strong> or repeated digits like <strong>111111</strong>.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Old PIN — only shown when user already has one */}
            {hasPin && (
              <PinInput
                id="oldPin"
                label="Old PIN"
                value={oldPin}
                onChange={setOldPin}
                show={showOld}
                onToggle={() => setShowOld(!showOld)}
                placeholder="Current PIN"
              />
            )}

            {/* New PIN */}
            <PinInput
              id="newPin"
              label="New PIN"
              value={newPin}
              onChange={setNewPin}
              show={showNew}
              onToggle={() => setShowNew(!showNew)}
              placeholder="New 6-digit PIN"
            />

            {/* PIN strength bar */}
            {newPin.length > 0 && strength && (
              <div className="-mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${strength.color}`}
                      style={{ width: strength.label === 'Too short' ? '33%' : strength.label === 'Weak — avoid patterns' ? '66%' : '100%' }}
                    />
                  </div>
                  <span className={`text-xs font-semibold ${strength.textColor}`}>{strength.label}</span>
                </div>
              </div>
            )}

            {/* Confirm PIN */}
            <PinInput
              id="conPin"
              label="Confirm PIN"
              value={confirmPin}
              onChange={setConfirmPin}
              show={showConfirm}
              onToggle={() => setShowConfirm(!showConfirm)}
              placeholder="Re-enter new PIN"
            />

            {/* Match indicator */}
            {confirmPin.length > 0 && (
              <div className={`flex items-center gap-2 text-xs font-semibold -mt-2 ${newPin === confirmPin ? 'text-green-600' : 'text-red-500'}`}>
                {newPin === confirmPin
                  ? <><CheckCircle size={13} /> PINs match</>
                  : <><AlertCircle size={13} /> PINs do not match</>
                }
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (confirmPin.length > 0 && newPin !== confirmPin)}
              className="w-full py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Updating…</>
              ) : (
                <><Lock size={18} /> Update PIN</>
              )}
            </button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-5">
          🔒 Your PIN is encrypted and never stored in plain text.
        </p>
      </div>
    </DashboardLayout>
  );
}
